import {useState, useEffect} from "react";
import {AnimatePresence, motion} from "framer-motion";
import Social from "../../components/social";
import Heading from "../../components/heading";
import { comparison } from "../../components/navbar";
import ColorBlocks from '../../components/ColorBlocks.jsx'
import {
  ABOUT,
  ALPHABET,
  CAROUSEL_MS,
  CAROUSEL_NOTE,
  LANGUAGES,
  WHAT_WE_DO
} from '../../data.js'
import background from "../../images/blue_background.jpg"
import "./Demo.css";


/*
 * The demonstration page.
 *
 * Type something in, press the button, and the page shows your text three
 * times over as rows of colored squares — once for each of the three
 * alphabets, ALPHA, BRAVO and CHARLIE. The three cards take turns on screen.
 *
 * Worth being clear about what this page is: it is a picture of the idea, made
 * in your browser. The colors here are picked at random each time you press
 * the button. Nothing is sent anywhere and nothing is encrypted. If you want
 * to see the real thing running, the "How your data is protected" page does
 * that instead — it sends your text to the server and shows you what actually
 * comes back.
 */

/**
 * Build one alphabet: every letter and digit gets a random color.
 *
 * A fresh one is built for each of the three cards, which is why the same word
 * looks different on each of them.
 */
function generateColorDictionary() {
  const dictionary = {}
  for (const character of ALPHABET) {
    const red = Math.floor(Math.random() * 256)
    const green = Math.floor(Math.random() * 256)
    const blue = Math.floor(Math.random() * 256)
    dictionary[character] = `rgb(${red}, ${green}, ${blue})`
  }
  return dictionary
}

/** Look every character of `text` up in `dictionary`, skipping anything it does not cover. */
function toColorBlocks(text, dictionary) {
  return [...text]
    .filter((character) => character in dictionary)
    .map((character) => ({ char: character, color: dictionary[character] }))
}

export default function Demo() {
  const [text, setText] = useState('')
  const [cards, setCards] = useState([])
  const [current, setCurrent] = useState(0)

  const hasRun = cards.length > 0

  // Once there is something to show, rotate through the three cards.
  useEffect(() => {
    if (!hasRun) return undefined
    const timer = setInterval(
      () => setCurrent((index) => (index + 1) % LANGUAGES.length),
      CAROUSEL_MS
    )
    return () => clearInterval(timer)
  }, [hasRun])

  function generate() {
    if (!text.trim()) return
    // One card per alphabet, each with its own set of colors.
    setCards(
      LANGUAGES.map((language) => ({
        language,
        blocks: toColorBlocks(text, generateColorDictionary())
      }))
    )
    setCurrent(0)
  }

  function clear() {
    setCards([])
    setCurrent(0)
  }

  const card = cards[current]

  return (
    <>
      <Heading />
      <img src={background} alt="" className="bg-image" />

      <main className="demo-page">
        <section className="panel generator tiedyebg">
          <h1 className="demo-heading">
            Enter text below to see how Colorantí encrypts your data
          </h1>

          <div className="demo-input-container">
            <input
              className="color-text"
              placeholder="ENTER TEXT"
              value={text}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={(event) => event.key === 'Enter' && generate()}
              aria-label="Text to turn into colors"
            />

            <div className="button-row">
              <button
                className="demo-button"
                onClick={generate}
                disabled={!text.trim()}
              >
                GENERATE COLORANTÍ
              </button>
              {hasRun && (
                <button className="demo-button ghost" onClick={clear}>
                  CLEAR
                </button>
              )}
            </div>
          </div>
        </section>

        <motion.section
          className="panel card"
          animate={{ y: hasRun ? -8 : 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <div className="demo-line" />
          <h2 className="card-heading">{ABOUT.heading}</h2>
          <p>{ABOUT.body}</p>
          <div className="demo-line" />
        </motion.section>

        <AnimatePresence>
          {hasRun && (
            <>
              <motion.section
                className="panel card"
                initial={{ opacity: 0, x: -60 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -60 }}
                transition={{ duration: 0.6, ease: 'easeOut', delay: 0.15 }}
              >
                <div className="demo-line" />
                <h2 className="card-heading">{WHAT_WE_DO.heading}</h2>
                <p>{WHAT_WE_DO.body}</p>
                <div className="demo-line" />
              </motion.section>

              <motion.section
                className="panel results tiedyebg"
                initial={{ opacity: 0, y: 60 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 60 }}
                transition={{ duration: 0.6, ease: 'easeOut', delay: 0.25 }}
              >
                <h2 className="demo-heading">Your text, in color</h2>

                {/* Clicking a name jumps straight to that card instead of
                    waiting for it to come round. */}
                <div className="language-legend">
                  <span className="legend-label">Alphabet:</span>
                  {LANGUAGES.map((language, index) => (
                    <button
                      key={language}
                      className={`legend-chip${index === current ? ' is-on' : ''}`}
                      onClick={() => setCurrent(index)}
                    >
                      {language}
                    </button>
                  ))}
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={card.language}
                    className="run"
                    initial={{ opacity: 0, x: 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -40 }}
                    transition={{ duration: 0.4, ease: 'easeOut' }}
                  >
                    <h3 className="run-label">{card.language}</h3>
                    <ColorBlocks blocks={card.blocks} />
                  </motion.div>
                </AnimatePresence>

                <p className="salt-note">{CAROUSEL_NOTE}</p>

                {/* TODO: point this at the "How your data is protected" page
                    once it is added to this repo. */}
                <button onClick={comparison} className="next">
                  next →
                </button>
              </motion.section>
            </>
          )}
        </AnimatePresence>
      </main>

      <Social />
    </>
  )
}
